import { createServerFn } from "@tanstack/react-start";

import { contentSource } from "@/content/source";
import type { LandingBlock, LandingPage, UseCaseCard } from "./types";

/**
 * Every use-case page's card, in the order the published index prints them.
 *
 * The cards are authored on the pages they describe, so this is a read of the
 * same CMS query the pages themselves come from rather than a second list. The
 * category pages need it because they show the cards of pages other than
 * themselves; a landing page does not, because its own blocks arrive with their
 * cards already joined on.
 *
 * Not named `useCaseCards`: a function whose name starts with `use` reads to the
 * hooks lint rule as a React hook, and this is a server function.
 */
export const publishedCaseCards = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<UseCaseCard>> => {
    const pages = await (await contentSource()).loadLandingPages();
    return (
      indexCards(pages) ??
      pages.flatMap((page) =>
        page.card ? [{ ...page.card, path: page.path }] : []
      )
    );
  }
);

type UseCaseIndex = Extract<LandingBlock, { kind: "useCaseIndex" }>;

/** An index block that arrived with its cards joined on. */
const carriesCards = (block: LandingBlock): block is UseCaseIndex =>
  block.kind === "useCaseIndex" && block.cards !== undefined;

/** The index block's cards, which arrive with every card joined on, in the published order. */
function indexCards(
  pages: ReadonlyArray<LandingPage>
): Array<UseCaseCard> | undefined {
  const index = pages.flatMap((page) => page.blocks).find(carriesCards);
  return index?.cards ? [...index.cards] : undefined;
}
